// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@steiner/XDL/types";
import { css } from "styled-components";

export const fontConfig = {
    url: "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&display=swap",
    weight: {
        regular: 400,
        semiBold: 600,
        bold: 700,
    },
    headingFontFamily: "'Montserrat', sans-serif",
    bodyFontFamily: "'Montserrat', sans-serif",
    headingColor: "text.heading",
    fontSize: {
        xxSmall: 10,
        xSmall: 12,
        small: 14,
        regular: 16,
    },
    lineHeight: {
        xxSmall: "13px",
        xSmall: "15px",
        small: "22px",
        regular: "24px",
    },
};

export default {
    fontFamilyImportUrl: "",
    body: {
        size: "16px",
        weight: fontConfig.weight.regular,
        fontFamily: fontConfig.bodyFontFamily,
        lineHeight: "24px",
    },
    p: {
        size: "16px",
        weight: fontConfig.weight.regular,
        lineHeight: "24px",
        css: css`
            display: block;
            letter-spacing: 0;
            margin-bottom: 16px;
        `,
    },
    h1: {
        size: "32px",
        weight: fontConfig.weight.regular,
        fontFamily: fontConfig.headingFontFamily,
        color: fontConfig.headingColor,
        lineHeight: "39px",
        css: css`
            letter-spacing: 0;
            margin-bottom: 20px;
            display: block;
        `,
    },
    h2: {
        size: "26px",
        weight: fontConfig.weight.semiBold,
        fontFamily: fontConfig.headingFontFamily,
        color: fontConfig.headingColor,
        lineHeight: "32px",
        css: css`
            letter-spacing: 0;
            margin-bottom: 16px;
            display: block;
        `,
    },
    h3: {
        size: "20px",
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.headingFontFamily,
        color: fontConfig.headingColor,
        lineHeight: "24px",
        css: css`
            letter-spacing: 0;
            margin-bottom: 12px;
            display: block;
        `,
    },
    h4: {
        size: "18px",
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.headingFontFamily,
        color: fontConfig.headingColor,
        lineHeight: "22px",
        css: css`
            letter-spacing: 0;
            margin-bottom: 8px;
            display: block;
        `,
    },
    h5: {
        size: "16px",
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.headingFontFamily,
        color: fontConfig.headingColor,
        lineHeight: "19px",
        css: css`
            letter-spacing: 0;
            margin-bottom: 8px;
            display: block;
        `,
    },
    h6: {
        size: "14px",
        weight: fontConfig.weight.bold,
        fontFamily: fontConfig.headingFontFamily,
        color: fontConfig.headingColor,
        lineHeight: "18px",
        css: css`
            letter-spacing: 0;
            margin-bottom: 8px;
            display: block;
        `,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "24px",
        fontFamily: fontConfig.bodyFontFamily,
        color: fontConfig.headingColor,
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "16px",
        fontFamily: fontConfig.bodyFontFamily,
        color: fontConfig.headingColor,
        transform: "uppercase",
        weight: fontConfig.weight.bold,
    },
    headerTertiary: {
        forwardAs: "p",
        size: "16px",
        fontFamily: fontConfig.bodyFontFamily,
        color: fontConfig.headingColor,
        transform: "uppercase",
    },
    legend: {
        forwardAs: "span",
        size: "10px",
        color: "text.main",
        transform: "uppercase",
        weight: fontConfig.weight.bold,
    },
} as xBaseTheme["typography"];
